import { STOCK_RESOURCES, type BuildingTypeId, type ResourceAmounts, type StockResource } from '../sim/buildings/definitions';
import type { ActionBus } from '../input/actions';
import { PASSABILITY_COLORS } from '../render/palette';
import type { TileMetadata } from '../world/WorldMap';

export type InputMode = 'mouse' | 'touch';

export interface HudOptions {
  readonly title: string;
  readonly phaseLabel: string;
  readonly buttonZoomStep: number;
  /** Starting input mode (from the device); switches live when the player uses the other one. */
  readonly inputMode: InputMode;
}

/** What the HUD needs to show about a tile; built by the scene from WorldMap metadata. */
export interface TileInspection extends TileMetadata {
  readonly swatch: string;
}

/** What the HUD shows about a placed building. */
export interface BuildingInspection {
  readonly id: number;
  readonly name: string;
  readonly category: string;
  readonly purpose: string;
  readonly state: 'under_construction' | 'complete';
  readonly progress: number;
  readonly health: number;
  readonly maxHealth: number;
  readonly origin: { x: number; y: number };
  readonly footprint: { width: number; height: number };
  readonly operationalFromPhase: number;
  readonly cost: string;
  /** Economy lines for the card (each optional). */
  readonly economy: {
    readonly production?: string;
    readonly storage?: string;
    readonly upkeep?: string;
    /** Shelter, e.g. "8 survivors". */
    readonly housing?: string;
    /** What demolishing returns, e.g. "15 wood". */
    readonly refund?: string;
  };
  /** Live production status text, e.g. "Next batch in 4 s" (updated via updateBuildingEconomy). */
  readonly economyStatus?: string;
  /** Workplace staffing (only for buildings with jobs). `editable` = the local player owns it. */
  readonly workers?: { readonly current: number; readonly slots: number; readonly editable: boolean; readonly canAdd: boolean };
  /** Phase in which the building's main function arrives, if later than the current build. */
  readonly pendingFunctionPhase?: number;
  /** Barracks / Workshop training (only when the local player owns it). */
  readonly training?: TrainingView;
  readonly demolishable: boolean;
  readonly preplaced: boolean;
  /** Phase 8: defence lines (armour, turret, power), upgrade offer / progress, repair hint. */
  readonly defence?: readonly string[];
  readonly upgrade?: { readonly name: string; readonly cost: string; readonly seconds: number; readonly affordable: boolean; readonly progress: number | null; readonly done: boolean };
  readonly repairHint?: string;
  /** Owner shown on the card. `isYou` = the local player owns it. */
  readonly owner: { readonly id: string; readonly name: string; readonly css: string; readonly isYou: boolean; readonly hasController: boolean };
}

/** One entry in the construction bar. */
export interface BuildCatalogItem {
  readonly id: BuildingTypeId;
  readonly name: string;
  readonly hotkey: string;
  readonly size: string;
  readonly cost: ResourceAmounts;
  readonly costText: string;
  readonly thumbnail: string;
}

export interface TrainingView {
  readonly ready: boolean;
  readonly options: readonly { readonly unit: string; readonly name: string; readonly cost: ResourceAmounts; readonly seconds: number; readonly affordable: boolean }[];
  readonly queue: readonly { readonly name: string; readonly progress: number }[];
  readonly limit: number;
}

/** The unit card (one or more selected units). */
export interface UnitsView {
  readonly count: number;
  /** Set when exactly one unit is shown. */
  readonly single?: {
    readonly name: string;
    readonly role: string;
    readonly owner: string;
    readonly ownerCss: string;
    readonly isYou: boolean;
    readonly stats: string;
    readonly order: string;
    /** Infection status line ("Infected — …", "Bitten 1 / 3"), if any. */
    readonly status?: string;
    readonly statusBad?: boolean;
  };
  readonly groups: readonly { readonly name: string; readonly count: number }[];
  readonly health: number;
  readonly maxHealth: number;
  /** The local player can command these units. */
  readonly commandable: boolean;
  /** Stance of the shown units ('mixed' when a group disagrees). */
  readonly stance?: 'aggressive' | 'hold' | 'mixed';
  /** Attack-move is armed: the next tap on the map sends them. */
  readonly attackMoveArmed?: boolean;
}

/** The local player's survivors for the resource bar. */
export interface PopulationView {
  readonly population: number;
  readonly capacity: number;
  readonly starving: boolean;
}

export type ColonyStatus = 'Growing' | 'Stable' | 'Housing full' | 'Hungry' | 'Starving' | 'Empty';

/** The colony panel. */
export interface ColonyView {
  readonly status: ColonyStatus;
  readonly population: number;
  readonly capacity: number;
  readonly working: number;
  readonly idle: number;
  /** Food per minute at current staffing. */
  readonly foodIn: number;
  readonly foodOut: number;
  /** Plain-language line about the next newcomer. */
  readonly growth: string;
  readonly workplaces: readonly { readonly id: number; readonly name: string; readonly workers: number; readonly slots: number }[];
  /** The infected threat in one line (count, next horde). */
  readonly threat: string;
  /** Power balance line, e.g. "Power 5 / 10 used" (absent when the colony has none). */
  readonly power?: string;
  /** Your units currently Infected. */
  readonly infectedUnits: number;
}

const POP_ICON =
  '<svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="7" cy="6" r="2.6" fill="#d9c7a3"/><path d="M2.5 16c0-3 2-5 4.5-5s4.5 2 4.5 5z" fill="#8a9a6b"/><circle cx="13.5" cy="7" r="2.2" fill="#c7b38d"/><path d="M10 16c.3-2.6 1.8-4.2 3.5-4.2S17.2 13.4 17.5 16z" fill="#6f7f55"/></svg>';

/** The local player's stockpile for the resource bar. */
export interface ResourceView {
  readonly amounts: Readonly<Record<StockResource, number>>;
  readonly capacity: Readonly<Record<StockResource, number>>;
  /** Survivors could not be fed at the last upkeep. */
  readonly shortage: boolean;
}

const RES_LABEL: Record<StockResource, string> = { wood: 'Wood', scrap: 'Scrap', food: 'Food' };

/** Small inline icons (no external assets). */
export const RES_ICON: Record<StockResource, string> = {
  wood: '<svg viewBox="0 0 20 20" aria-hidden="true"><rect x="2" y="5" width="13" height="4.5" rx="2" fill="#a0703f"/><rect x="5" y="10.5" width="13" height="4.5" rx="2" fill="#8a5d33"/><circle cx="15" cy="7.25" r="2.1" fill="#d6ab72"/><circle cx="18" cy="12.75" r="2.1" fill="#c99a62"/></svg>',
  scrap: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 2.5l1.6 2.2 2.7-.6.4 2.7 2.4 1.4-1.3 2.4 1.3 2.4-2.4 1.4-.4 2.7-2.7-.6L10 17.5l-1.6-2.2-2.7.6-.4-2.7-2.4-1.4 1.3-2.4-1.3-2.4 2.4-1.4.4-2.7 2.7.6z" fill="#9aa0a6"/><circle cx="10" cy="10" r="2.6" fill="#3a3d41"/></svg>',
  food: '<svg viewBox="0 0 20 20" aria-hidden="true"><rect x="5" y="4" width="10" height="13" rx="2" fill="#b8573a"/><rect x="5" y="7" width="10" height="5" fill="#e8d6a4"/><rect x="5" y="3" width="10" height="2" rx="1" fill="#9ea3a8"/></svg>',
};

export function costHtml(cost: ResourceAmounts): string {
  const parts = STOCK_RESOURCES.filter((r) => (cost[r] ?? 0) > 0).map((r) => `<span class="cost-part">${RES_ICON[r]}${cost[r]}</span>`);
  return parts.length ? parts.join('') : '<span class="cost-part">Free</span>';
}

export interface BuildModeView {
  readonly active: BuildingTypeId | null;
  /** Validation message for the footprint under the cursor ('' = valid / no cursor). */
  readonly message: string;
  readonly valid: boolean | null;
  /** Preview was placed by touch and waits for the confirm button. */
  readonly pinned: boolean;
}

const PASSABILITY_LABEL = { buildable: 'Buildable', walkable: 'Walkable only', blocked: 'Blocked' } as const;

/**
 * HTML overlay UI. Every control emits a real GameAction on the bus; the HUD
 * never mutates game state and only displays what it is given.
 * No resource counters are shown: the resource ledger arrives in Phase 3.
 */
export class Hud {
  private readonly root: HTMLElement;
  private readonly zoomLabel: HTMLElement;
  private readonly gridBtn: HTMLButtonElement;
  private readonly passBtn: HTMLButtonElement;
  private readonly legend: HTMLElement;
  private readonly inspector: HTMLElement;
  private readonly cursor: HTMLElement;
  private readonly debugEl: HTMLElement;
  private readonly buildBar: HTMLElement;
  private readonly buildButtons: HTMLElement;
  private readonly buildStatus: HTMLElement;
  private readonly toastEl: HTMLElement;
  private readonly onClick: (e: MouseEvent) => void;
  private catalog: readonly BuildCatalogItem[] = [];
  private lastCursorText = '';
  private lastZoomText = '';
  private shownBuilding: number | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private mode: InputMode;
  private readonly sizeObserver: ResizeObserver | null;
  private readonly colonyEl: HTMLElement;
  private readonly selectBox: HTMLElement;
  private army = { count: 0, boxMode: false };
  private showingUnits = false;
  private lastUnitsKey = '';
  private readonly popEl: { root: HTMLElement; amt: HTMLElement; cap: HTMLElement };
  private lastPop = '';
  private lastColony = '';
  private resourceEls = new Map<StockResource, { root: HTMLElement; amt: HTMLElement; cap: HTMLElement }>();
  private lastResources = '';
  private lastBuildView: BuildModeView = { active: null, message: '', valid: null, pinned: false };

  constructor(container: HTMLElement, private readonly bus: ActionBus, opts: HudOptions) {
    this.root = container;
    this.mode = opts.inputMode;
    this.root.classList.toggle('input-touch', this.mode === 'touch');
    const legendRows = (Object.keys(PASSABILITY_COLORS) as (keyof typeof PASSABILITY_COLORS)[])
      .map((k) => `<li><span class="legend-swatch" style="background:${PASSABILITY_COLORS[k].css}"></span>${PASSABILITY_LABEL[k]}</li>`)
      .join('');
    this.root.innerHTML = `
      <header class="hud-bar" data-testid="top-bar">
        <div class="brand">
          <svg class="crest" viewBox="0 0 32 32" aria-hidden="true">
            <rect x="1" y="1" width="30" height="30" rx="6" fill="#1d2023" stroke="#f0b43c" stroke-width="2"/>
            <path d="M16 5v22M11 27l5-14 5 14M12.5 20h7" stroke="#f0b43c" stroke-width="2" fill="none" stroke-linecap="round"/>
            <path d="M9 9a10 10 0 0 1 14 0M11.5 11.5a6 6 0 0 1 9 0" stroke="#c8642a" stroke-width="1.8" fill="none" stroke-linecap="round"/>
          </svg>
          <span class="brand-name">${escapeHtml(opts.title)}</span>
          <span class="phase-pill">${escapeHtml(opts.phaseLabel)}</span>
        </div>
        <div class="resources" data-testid="resource-bar" role="group" aria-label="Resources">
          ${STOCK_RESOURCES.map(
            (r) => `<div class="res" data-res="${r}" data-testid="res-${r}" title="${RES_LABEL[r]}">
              ${RES_ICON[r]}<span class="res-amt" data-testid="res-${r}-amount">0</span><span class="res-cap" data-testid="res-${r}-cap">/0</span>
              <span class="sr-only">${RES_LABEL[r]}</span>
            </div>`,
          ).join('')}
          <button class="res res-pop" data-action="colony" data-testid="res-pop" aria-expanded="false" title="Colony (C)">
            ${POP_ICON}<span class="res-amt" data-testid="res-pop-amount">0</span><span class="res-cap" data-testid="res-pop-cap">/0</span>
            <span class="sr-only">Survivors — open colony</span>
          </button>
        </div>
        <div class="toolbar" role="toolbar" aria-label="View controls">
          <button class="btn icon" data-action="zoom-out" data-testid="zoom-out" aria-label="Zoom out" title="Zoom out (−)">−</button>
          <span class="zoom-label" data-testid="zoom-label" aria-live="polite">100%</span>
          <button class="btn icon" data-action="zoom-in" data-testid="zoom-in" aria-label="Zoom in" title="Zoom in (+)">+</button>
          <button class="btn" data-action="reset" data-testid="reset-view" title="Reset view (Home)" aria-label="Reset view"><span class="ico" aria-hidden="true">⌖</span><span class="lbl">Reset view</span></button>
          <button class="btn toggle" data-action="grid" data-testid="grid-toggle" aria-pressed="false" title="Toggle grid (G)">Grid</button>
          <button class="btn toggle" data-action="passability" data-testid="passability-toggle" aria-pressed="false" title="Show buildable / blocked tiles (P)">Terrain</button>
        </div>
      </header>
      <aside class="panel inspector" data-testid="inspector" aria-live="polite"></aside>
      <aside class="panel colony" data-testid="colony-panel" hidden></aside>
      <aside class="legend" data-testid="passability-legend" hidden>
        <h3 class="legend-title">Terrain</h3>
        <ul>${legendRows}</ul>
      </aside>
      <footer class="hint" data-testid="controls-hint">
        <span><kbd>Drag</kbd> pan</span><span><kbd>Wheel</kbd> zoom</span><span><kbd>Click</kbd> select</span>
        <span><kbd>1–0</kbd> build</span><span><kbd>Esc</kbd> cancel</span><span><kbd>Shift+drag</kbd>/<kbd>B</kbd> box</span><span><kbd>Q</kbd> army</span><span><kbd>Right-click</kbd> order</span><span><kbd>F</kbd> attack-move</span><span><kbd>H</kbd> hold</span>
        <span><kbd>WASD</kbd> move</span><span><kbd>G</kbd> grid</span><span><kbd>P</kbd> terrain</span><span><kbd>\`</kbd> debug</span>
      </footer>
      <footer class="hint hint-touch" data-testid="touch-hint">
        <span><kbd>Drag</kbd> move</span><span><kbd>Pinch</kbd> zoom</span><span><kbd>Tap</kbd> select / order</span>
      </footer>
      <section class="build-bar" data-testid="build-bar" aria-label="Construction">
        <div class="build-status" data-testid="build-status" aria-live="polite"></div>
        <div class="build-buttons" role="toolbar" aria-label="Buildings"></div>
      </section>
      <div class="toast" data-testid="toast" role="status" aria-live="polite" hidden></div>
      <div class="match-timer" data-testid="match-timer" hidden></div>
      <section class="result" data-testid="result" role="dialog" aria-modal="true" aria-labelledby="result-title" hidden></section>
      <div class="select-box" data-testid="select-box" hidden></div>
      <div class="cursor-readout" data-testid="cursor-readout" aria-hidden="true">—</div>
      <pre class="debug" data-testid="debug" hidden></pre>
    `;
    this.zoomLabel = this.q('[data-testid="zoom-label"]');
    this.gridBtn = this.q<HTMLButtonElement>('[data-testid="grid-toggle"]');
    this.passBtn = this.q<HTMLButtonElement>('[data-testid="passability-toggle"]');
    this.legend = this.q('[data-testid="passability-legend"]');
    this.inspector = this.q('[data-testid="inspector"]');
    this.cursor = this.q('[data-testid="cursor-readout"]');
    this.debugEl = this.q('[data-testid="debug"]');
    this.buildBar = this.q('[data-testid="build-bar"]');
    this.buildButtons = this.q('.build-buttons');
    this.buildStatus = this.q('[data-testid="build-status"]');
    this.toastEl = this.q('[data-testid="toast"]');
    this.timerEl = this.q('[data-testid="match-timer"]');
    this.resultEl = this.q('[data-testid="result"]');
    this.colonyEl = this.q('[data-testid="colony-panel"]');
    this.selectBox = this.q('[data-testid="select-box"]');
    this.popEl = { root: this.q('[data-testid="res-pop"]'), amt: this.q('[data-testid="res-pop-amount"]'), cap: this.q('[data-testid="res-pop-cap"]') };
    for (const r of STOCK_RESOURCES) {
      this.resourceEls.set(r, {
        root: this.q(`[data-testid="res-${r}"]`),
        amt: this.q(`[data-testid="res-${r}-amount"]`),
        cap: this.q(`[data-testid="res-${r}-cap"]`),
      });
    }

    const step = opts.buttonZoomStep;
    this.onClick = (e: MouseEvent): void => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!btn) return;
      switch (btn.dataset.action) {
        case 'zoom-in': this.bus.emit({ type: 'zoom', factor: step, screen: null }); break;
        case 'zoom-out': this.bus.emit({ type: 'zoom', factor: 1 / step, screen: null }); break;
        case 'reset': this.bus.emit({ type: 'resetView' }); break;
        case 'grid': this.bus.emit({ type: 'toggleGrid' }); break;
        case 'passability': this.bus.emit({ type: 'togglePassability' }); break;
        case 'deselect': this.bus.emit({ type: 'clearSelection' }); break;
        case 'cancel-build': this.bus.emit({ type: 'cancel' }); break;
        case 'confirm-build': this.bus.emit({ type: 'confirmBuild' }); break;
        case 'colony': this.bus.emit({ type: 'toggleColony' }); break;
        case 'box-mode': this.bus.emit({ type: 'toggleBoxMode' }); break;
        case 'select-army': this.bus.emit({ type: 'selectArmy' }); break;
        case 'stop-units': this.bus.emit({ type: 'stopUnits' }); break;
        case 'deselect-units': this.bus.emit({ type: 'deselectUnits' }); break;
        case 'attack-move': this.bus.emit({ type: 'toggleAttackMove' }); break;
        case 'play-again': this.bus.emit({ type: 'playAgain' }); break;
        case 'close-result': this.showResult(null); break;
        case 'upgrade': {
          const id = Number(btn.dataset.id);
          if (Number.isInteger(id)) this.bus.emit({ type: 'upgradeBuilding', id });
          break;
        }
        case 'stance': {
          const st = btn.dataset.stance;
          if (st === 'aggressive' || st === 'hold') this.bus.emit({ type: 'setStance', stance: st });
          break;
        }
        case 'train': {
          const id = Number(btn.dataset.id);
          const unit = btn.dataset.unit;
          if (Number.isInteger(id) && (unit === 'ranger' || unit === 'heavy' || unit === 'engineer')) this.bus.emit({ type: 'trainUnit', buildingId: id, unit });
          break;
        }
        case 'cancel-training': {
          const id = Number(btn.dataset.id);
          if (Number.isInteger(id)) this.bus.emit({ type: 'cancelTraining', buildingId: id });
          break;
        }
        case 'workers': {
          const id = Number(btn.dataset.id);
          const delta = Number(btn.dataset.delta);
          if (Number.isInteger(id) && (delta === 1 || delta === -1)) this.bus.emit({ type: 'adjustWorkers', id, delta });
          break;
        }
        case 'build': {
          const id = this.catalog.find((c) => c.id === btn.dataset.building)?.id;
          if (id) this.bus.emit({ type: 'selectBuild', building: id });
          break;
        }
        case 'demolish': {
          const id = Number(btn.dataset.id);
          if (Number.isInteger(id)) this.bus.emit({ type: 'demolish', id });
          break;
        }
        default: break;
      }
    };
    this.root.addEventListener('click', this.onClick);
    // Publish the real heights of the top bar and construction bar as CSS variables so
    // panels, toasts and sheets sit between them in every orientation without guessing.
    const topBar = this.q('[data-testid="top-bar"]');
    const publish = (): void => {
      this.root.style.setProperty('--top-bar-h', `${Math.ceil(topBar.getBoundingClientRect().height)}px`);
      this.root.style.setProperty('--build-bar-h', `${Math.ceil(this.buildBar.getBoundingClientRect().height)}px`);
    };
    this.sizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    this.sizeObserver?.observe(topBar);
    this.sizeObserver?.observe(this.buildBar);
    publish();
    this.setInspection(null);
    this.setBuildMode(this.lastBuildView);
  }

  // ---------------------------------------------------------------- input mode

  get inputMode(): InputMode {
    return this.mode;
  }

  /** Touch hides keyboard hints / hotkey badges and shows the build confirm flow. */
  setInputMode(m: InputMode): void {
    if (m === this.mode) return;
    this.mode = m;
    this.root.classList.toggle('input-touch', m === 'touch');
    this.setBuildMode(this.lastBuildView);
  }

  /** Screen rect of the inspector when it is showing something (null when empty / hidden). */
  inspectorRect(): DOMRect | null {
    if (this.inspector.classList.contains('is-empty')) return null;
    const r = this.inspector.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? r : null;
  }

  /** Bottom edge of the top bar in screen px. */
  topBarBottom(): number {
    return this.q('[data-testid="top-bar"]').getBoundingClientRect().bottom;
  }

  // ---------------------------------------------------------------- view controls

  /** Called on every camera change, so it only touches the DOM when the text changes. */
  setZoom(zoom: number): void {
    const t = `${Math.round(zoom * 100)}%`;
    if (t === this.lastZoomText) return;
    this.lastZoomText = t;
    this.zoomLabel.textContent = t;
  }

  setGrid(on: boolean): void {
    this.gridBtn.setAttribute('aria-pressed', String(on));
  }

  setPassability(on: boolean): void {
    this.passBtn.setAttribute('aria-pressed', String(on));
    this.legend.hidden = !on;
  }

  /** Tile under the pointer (or null). `occupant` names a building standing on it. */
  setCursor(t: TileMetadata | null, pointerOnCanvas: boolean, occupant: string | null = null): void {
    const text = t ? `${t.x}, ${t.y} · ${occupant ?? t.terrain.name}` : pointerOnCanvas ? 'Off map' : '—';
    if (text === this.lastCursorText) return;
    this.lastCursorText = text;
    this.cursor.textContent = text;
    this.cursor.classList.toggle('blocked', !!t && !occupant && t.passability === 'blocked');
  }

  setDebug(text: string | null): void {
    this.debugEl.hidden = text === null;
    if (text !== null) this.debugEl.textContent = text;
  }

  get debugVisible(): boolean {
    return !this.debugEl.hidden;
  }

  // ---------------------------------------------------------------- resources

  /** Update the resource bar (only touches the DOM when something changed). */
  setResources(v: ResourceView): void {
    const key = JSON.stringify(v);
    if (key === this.lastResources) return;
    this.lastResources = key;
    for (const r of STOCK_RESOURCES) {
      const el = this.resourceEls.get(r)!;
      el.amt.textContent = String(v.amounts[r]);
      el.cap.textContent = `/${v.capacity[r]}`;
      el.root.classList.toggle('is-full', v.amounts[r] >= v.capacity[r]);
      el.root.classList.toggle('is-short', r === 'food' && v.shortage);
      el.root.setAttribute('aria-label', `${RES_LABEL[r]} ${v.amounts[r]} of ${v.capacity[r]}${v.amounts[r] >= v.capacity[r] ? ', storage full' : ''}`);
    }
  }

  setPopulation(v: PopulationView): void {
    const key = JSON.stringify(v);
    if (key === this.lastPop) return;
    this.lastPop = key;
    this.popEl.amt.textContent = String(v.population);
    this.popEl.cap.textContent = `/${v.capacity}`;
    this.popEl.root.classList.toggle('is-full', v.population >= v.capacity);
    this.popEl.root.classList.toggle('is-short', v.starving);
    this.popEl.root.setAttribute('aria-label', `Survivors ${v.population} of ${v.capacity}. Open colony.`);
  }

  get colonyOpen(): boolean {
    return !this.colonyEl.hidden;
  }

  /** Show the colony panel with this content, or hide it (null). Hides the inspector while open. */
  setColony(v: ColonyView | null): void {
    this.popEl.root.setAttribute('aria-expanded', String(v !== null));
    this.root.classList.toggle('colony-open', v !== null);
    if (!v) {
      this.colonyEl.hidden = true;
      this.lastColony = '';
      return;
    }
    this.colonyEl.hidden = false;
    // The threat / power lines change every second (horde countdown): update them in place, so the
    // panel (and its buttons) is only rebuilt when something else changes.
    const { threat, power, infectedUnits, ...rest } = v;
    const key = JSON.stringify(rest) + (power === undefined ? '' : '+power') + (infectedUnits ? '+sick' : '');
    if (key === this.lastColony) {
      const t = this.colonyEl.querySelector<HTMLElement>('[data-testid="colony-threat"]');
      const pw = this.colonyEl.querySelector<HTMLElement>('[data-testid="colony-power"]');
      const threatHtml = `${escapeHtml(threat)}${infectedUnits ? ` <span class="bad">· ${infectedUnits} of your units infected</span>` : ''}`;
      if (t && t.innerHTML !== threatHtml) t.innerHTML = threatHtml;
      if (pw && power !== undefined && pw.textContent !== power) pw.textContent = power;
      return;
    }
    this.lastColony = key;
    const net = v.foodIn - v.foodOut;
    const fmt = (n: number) => (Math.round(n * 10) / 10).toString();
    const statusClass = { Growing: 'ok', Stable: 'ok', 'Housing full': 'warn', Hungry: 'warn', Starving: 'bad', Empty: 'bad' }[v.status];
    this.colonyEl.innerHTML = `
      <div class="panel-head">
        <h2 class="panel-title">Colony</h2>
        <button class="btn icon small" data-action="colony" aria-label="Close colony">×</button>
      </div>
      <div class="colony-status ${statusClass}" data-testid="colony-status">${escapeHtml(v.status)}</div>
      <dl class="colony-grid">
        <div><dt>Survivors</dt><dd data-testid="colony-population">${v.population} / ${v.capacity}</dd></div>
        <div><dt>Working</dt><dd data-testid="colony-working">${v.working}</dd></div>
        <div><dt>Idle</dt><dd data-testid="colony-idle">${v.idle}</dd></div>
        <div><dt>Food / min</dt><dd data-testid="colony-food" class="${net < 0 ? 'neg' : 'pos'}">+${fmt(v.foodIn)} −${fmt(v.foodOut)} = ${net >= 0 ? '+' : ''}${fmt(net)}</dd></div>
      </dl>
      <p class="colony-growth" data-testid="colony-growth">${escapeHtml(v.growth)}</p>
      <h3 class="colony-sub">Threat</h3>
      ${v.power ? `<p class="colony-threat" data-testid="colony-power">${escapeHtml(v.power)}</p>` : ''}
      <p class="colony-threat" data-testid="colony-threat">${escapeHtml(v.threat)}${v.infectedUnits ? ` <span class="bad">· ${v.infectedUnits} of your units infected</span>` : ''}</p>
      ${
        v.workplaces.length
          ? `<h3 class="colony-sub">Workplaces</h3><ul class="colony-jobs" data-testid="colony-jobs">${v.workplaces
              .map((w) => `<li><span>${escapeHtml(w.name)}</span><span class="${w.workers === 0 ? 'bad' : ''}">${w.workers} / ${w.slots}</span></li>`)
              .join('')}</ul>`
          : '<p class="muted small">No workplaces yet — build a Farm, Sawmill or Scrap Yard.</p>'
      }`;
  }

  /** Dim construction buttons the player cannot pay for right now. */
  setAffordable(affordable: ReadonlySet<BuildingTypeId>): void {
    for (const btn of this.buildButtons.querySelectorAll<HTMLElement>('.build-btn')) {
      btn.classList.toggle('is-unaffordable', !affordable.has(btn.dataset.building as BuildingTypeId));
    }
  }

  // ---------------------------------------------------------------- construction

  setBuildCatalog(items: readonly BuildCatalogItem[]): void {
    this.catalog = items;
    this.buildButtons.innerHTML = items
      .map(
        (b) => `
        <button class="build-btn" data-action="build" data-building="${b.id}" data-testid="build-${b.id}" aria-pressed="false" title="${escapeHtml(b.name)} (${b.hotkey})">
          <span class="build-key">${escapeHtml(b.hotkey)}</span>
          ${b.thumbnail ? `<img class="build-thumb" src="${b.thumbnail}" alt="" draggable="false">` : ''}
          <span class="build-name">${escapeHtml(b.name)}</span>
          <span class="build-cost-line" data-testid="build-cost-${b.id}">${costHtml(b.cost)}</span>
        </button>`,
      )
      .join('');
  }

  setBuildMode(v: BuildModeView): void {
    this.lastBuildView = v;
    for (const btn of this.buildButtons.querySelectorAll<HTMLElement>('.build-btn')) {
      btn.setAttribute('aria-pressed', String(btn.dataset.building === v.active));
    }
    this.buildBar.classList.toggle('is-placing', v.active !== null);
    if (!v.active) {
      this.buildStatus.innerHTML = `<span class="muted build-idle">${this.mode === 'touch' ? 'Construction — tap a building' : 'Construction — choose a building (1–0)'}</span>${this.armyHtml()}`;
      return;
    }
    const item = this.catalog.find((c) => c.id === v.active);
    const confirmFlow = v.pinned || this.mode === 'touch';
    const okText = confirmFlow ? 'Valid site — drag to adjust, ✓ to build' : 'Valid site — click to build';
    const state =
      v.valid === null
        ? confirmFlow ? '<span class="muted">Tap the map to choose a spot</span>' : ''
        : v.valid
          ? `<span class="ok">${okText}</span>`
          : `<span class="bad" data-testid="placement-error">${escapeHtml(v.message)}</span>`;
    const actions = confirmFlow
      ? `<span class="build-actions">
          <button class="btn confirm" data-action="confirm-build" data-testid="confirm-build" aria-label="Build here" ${v.valid ? '' : 'disabled'}>✓ Build</button>
          <button class="btn cancel" data-action="cancel-build" data-testid="cancel-build" aria-label="Cancel building">✕</button>
        </span>`
      : `<button class="btn small-text" data-action="cancel-build" data-testid="cancel-build">Cancel</button>`;
    this.buildStatus.innerHTML = `
      <strong>${escapeHtml(item?.name ?? v.active)}</strong>
      <span class="muted build-cost">${escapeHtml(item?.size ?? '')} · cost ${escapeHtml(item?.costText ?? '—')}</span>
      ${state}
      ${actions}`;
  }

  /**
   * Show a short message. Messages queue (each stays at least 1.2 s, the last
   * one 2.6 s) so a burst — a horde arriving while food runs out — never hides
   * the important one. Repeats of the message on screen just extend it; at
   * most 3 wait.
   */
  toast(message: string, kind: 'ok' | 'error' | 'info' = 'info'): void {
    this.toastLog.push(message);
    if (this.toastLog.length > 40) this.toastLog.shift();
    if (!this.toastEl.hidden && this.toastEl.textContent === message) {
      this.scheduleToastEnd();
      return;
    }
    if (this.toastQueue.some((t) => t.message === message)) return;
    if (this.toastEl.hidden || performance.now() - this.toastShownAt >= 1200) {
      this.showToast(message, kind);
      return;
    }
    this.toastQueue.push({ message, kind });
    if (this.toastQueue.length > 3) this.toastQueue.shift();
    this.scheduleToastEnd();
  }

  private timerEl!: HTMLElement;
  private resultEl!: HTMLElement;

  /** Timed modes (Raid): seconds left, or null to hide. */
  setTimer(seconds: number | null): void {
    if (seconds === null) {
      this.timerEl.hidden = true;
      return;
    }
    const s = Math.ceil(seconds);
    const text = `⏱ ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    if (this.timerEl.textContent !== text) this.timerEl.textContent = text;
    this.timerEl.hidden = false;
    this.timerEl.classList.toggle('urgent', s <= 60);
  }

  /** End-of-match screen (Phase 9). `null` hides it. */
  showResult(v: { title: string; outcome: 'victory' | 'defeat' | 'draw'; reason: string; lines: readonly string[] } | null): void {
    if (!v) {
      this.resultEl.hidden = true;
      return;
    }
    this.resultEl.dataset.outcome = v.outcome;
    this.resultEl.innerHTML = `
      <div class="result-card">
        <h2 id="result-title" class="result-title" data-testid="result-title">${escapeHtml(v.title)}</h2>
        <p class="result-reason" data-testid="result-reason">${escapeHtml(v.reason)}</p>
        <ul class="result-lines">${v.lines.map((l) => `<li>${escapeHtml(l)}</li>`).join('')}</ul>
        <div class="result-actions">
          <button class="btn" data-action="play-again" data-testid="play-again">Play again</button>
          <button class="btn" data-action="close-result" data-testid="close-result">Look around</button>
        </div>
      </div>`;
    this.resultEl.hidden = false;
  }

  /** Every message toasted recently, oldest first (debug / tests). */
  get recentToasts(): readonly string[] {
    return this.toastLog;
  }

  private readonly toastLog: string[] = [];
  private readonly toastQueue: { message: string; kind: 'ok' | 'error' | 'info' }[] = [];
  private toastShownAt = 0;

  private showToast(message: string, kind: 'ok' | 'error' | 'info'): void {
    this.toastEl.textContent = message;
    this.toastEl.dataset.kind = kind;
    this.toastEl.hidden = false;
    this.toastShownAt = performance.now();
    this.scheduleToastEnd();
  }

  private scheduleToastEnd(): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    const wait = this.toastQueue.length ? Math.max(0, 1200 - (performance.now() - this.toastShownAt)) : 2600;
    this.toastTimer = setTimeout(() => {
      this.toastTimer = null;
      const next = this.toastQueue.shift();
      if (next) this.showToast(next.message, next.kind);
      else this.toastEl.hidden = true;
    }, wait);
  }

  // ---------------------------------------------------------------- army & units

  /** Army controls in the construction bar: Box mode and select-all. */
  setArmy(count: number, boxMode: boolean): void {
    if (count === this.army.count && boxMode === this.army.boxMode) return;
    this.army = { count, boxMode };
    this.root.classList.toggle('box-mode', boxMode);
    if (!this.lastBuildView.active) this.setBuildMode(this.lastBuildView);
  }

  private armyHtml(): string {
    return `<span class="army-controls">
      <button class="btn army-btn" data-action="box-mode" data-testid="box-mode" aria-pressed="${this.army.boxMode}" title="Box select (B) — drag to select units">▢ Box</button>
      <button class="btn army-btn" data-action="select-army" data-testid="select-army" ${this.army.count ? '' : 'disabled'} title="Select all units (Q)">Army ${this.army.count}</button>
    </span>`;
  }

  /** The drag rectangle for box selection (screen px), or hide it. */
  setSelectBox(r: { x: number; y: number; w: number; h: number } | null): void {
    this.selectBox.hidden = r === null;
    if (r) Object.assign(this.selectBox.style, { left: `${r.x}px`, top: `${r.y}px`, width: `${r.w}px`, height: `${r.h}px` });
  }

  get unitsShown(): boolean {
    return this.showingUnits;
  }

  /** Show the unit card in the inspector, or (null) give the inspector back to tiles / buildings. */
  setUnits(v: UnitsView | null): void {
    if (!v) {
      this.lastUnitsKey = '';
      if (this.showingUnits) {
        this.showingUnits = false;
        this.setInspection(null);
      }
      return;
    }
    // Only re-render when something changed, so buttons are not swapped out under a finger mid-tap.
    const key = JSON.stringify(v) + this.mode;
    if (this.showingUnits && key === this.lastUnitsKey) return;
    this.lastUnitsKey = key;
    this.showingUnits = true;
    this.shownBuilding = null;
    this.inspector.classList.remove('is-empty');
    const frac = v.maxHealth ? v.health / v.maxHealth : 0;
    const one = v.single;
    this.inspector.innerHTML = `
      <div class="panel-head">
        <h2 class="panel-title">${v.count === 1 ? 'Unit' : `${v.count} units`}</h2>
        <button class="btn icon small" data-action="deselect-units" data-testid="deselect-units" aria-label="Deselect units" title="Deselect (Esc)">×</button>
      </div>
      ${
        one
          ? `<div class="bld-name" data-testid="unit-name">${escapeHtml(one.name)}</div>
             <div class="owner-row" data-testid="unit-owner"><span class="owner-dot" style="background:${one.ownerCss}"></span>${escapeHtml(one.isYou ? 'Your colony' : one.owner)}</div>
             <p class="desc">${escapeHtml(one.role)}</p>`
          : `<ul class="unit-groups" data-testid="unit-groups">${v.groups.map((g) => `<li><span>${escapeHtml(g.name)}</span><b>× ${g.count}</b></li>`).join('')}</ul>`
      }
      <div class="hp" aria-label="Health"><div class="hp-fill" style="width:${100 * frac}%"></div></div>
      <div class="hp-text muted" data-testid="unit-health">Health ${v.health} / ${v.maxHealth}</div>
      ${one?.status ? `<p class="unit-status ${one.statusBad ? 'bad' : 'warn'}" data-testid="unit-status">${escapeHtml(one.status)}</p>` : ''}
      ${one ? `<p class="muted small" data-testid="unit-stats">${escapeHtml(one.stats)}</p><p class="econ-status" data-testid="unit-order">${escapeHtml(one.order)}</p>` : ''}
      ${
        v.commandable
          ? `<div class="stance-row" role="group" aria-label="Stance" data-testid="stance">
               <span class="muted small">Stance</span>
               <button class="btn small-text seg ${v.stance === 'aggressive' ? 'is-on' : ''}" data-action="stance" data-stance="aggressive" data-testid="stance-aggressive" aria-pressed="${v.stance === 'aggressive'}" title="Aggressive — engage enemies in sight (H toggles)">Aggressive</button>
               <button class="btn small-text seg ${v.stance === 'hold' ? 'is-on' : ''}" data-action="stance" data-stance="hold" data-testid="stance-hold" aria-pressed="${v.stance === 'hold'}" title="Hold — stay put, shoot only what is in range (H toggles)">Hold</button>
             </div>
             <p class="note unit-hint ${v.attackMoveArmed ? 'is-armed' : ''}" data-testid="unit-hint">${
               v.attackMoveArmed
                 ? this.mode === 'touch'
                   ? 'Attack-move: tap where to go — they fight anything on the way.'
                   : 'Attack-move: click where to go — they fight anything on the way (Esc cancels).'
                 : this.mode === 'touch'
                   ? 'Tap the ground to move · tap an enemy to attack.'
                   : 'Click (or right-click) the ground to move · an enemy to attack.'
             }</p>
             <div class="unit-actions">
               <button class="btn ${v.attackMoveArmed ? 'is-armed' : ''}" data-action="attack-move" data-testid="attack-move" aria-pressed="${!!v.attackMoveArmed}" title="Attack-move (F)">⚔ Attack-move</button>
               <button class="btn" data-action="stop-units" data-testid="stop-units" title="Stop (X)">Stop</button>
             </div>`
          : ''
      }`;
  }

  /** Live training queue of the shown building (updates only the queue and button states, never re-creates buttons). */
  updateTraining(id: number, t: TrainingView): void {
    if (this.shownBuilding !== id) return;
    const list = this.inspector.querySelector<HTMLElement>('[data-testid="training-queue"]');
    if (list) list.innerHTML = this.queueHtml(t);
    for (const btn of this.inspector.querySelectorAll<HTMLElement>('[data-action="train"]')) {
      const o = t.options.find((x) => x.unit === btn.dataset.unit);
      btn.classList.toggle('is-unaffordable', !!o && !o.affordable);
    }
    const cancel = this.inspector.querySelector<HTMLButtonElement>('[data-testid="cancel-training"]');
    if (cancel) cancel.disabled = t.queue.length === 0;
  }

  private queueHtml(t: TrainingView): string {
    if (!t.queue.length) return `<li class="muted">Nothing in training (up to ${t.limit}).</li>`;
    return t.queue
      .map((q, i) => `<li><span>${escapeHtml(q.name)}</span><span class="qbar"><span style="width:${i === 0 ? Math.round(q.progress * 100) : 0}%"></span></span></li>`)
      .join('');
  }

  private trainingHtml(id: number, t: TrainingView): string {
    if (!t.ready) return '<p class="note">Training starts once construction is finished.</p>';
    return `<div class="training" data-testid="training">
      <h3 class="colony-sub">Train</h3>
      <div class="train-options">${t.options
        .map(
          (o) => `<button class="btn train-btn${o.affordable ? '' : ' is-unaffordable'}" data-action="train" data-id="${id}" data-unit="${o.unit}" data-testid="train-${o.unit}" title="${escapeHtml(o.name)} — ${o.seconds} s">
            <span class="train-name">${escapeHtml(o.name)}</span>
            <span class="build-cost-line">${costHtml(o.cost)}<span class="cost-part">${POP_ICON}1</span></span>
          </button>`,
        )
        .join('')}</div>
      <ul class="train-queue" data-testid="training-queue">${this.queueHtml(t)}</ul>
      <button class="btn small-text" data-action="cancel-training" data-id="${id}" data-testid="cancel-training" ${t.queue.length ? '' : 'disabled'}>Cancel last</button>
    </div>`;
  }

  // ---------------------------------------------------------------- inspector

  setInspection(t: TileInspection | null, b: BuildingInspection | null = null): void {
    this.shownBuilding = b?.id ?? null;
    this.showingUnits = false;
    this.lastUnitsKey = '';
    if (!t) {
      this.inspector.classList.add('is-empty');
      this.inspector.innerHTML = `
        <h2 class="panel-title">Inspector</h2>
        <p class="muted">Click a tile or building to inspect it.</p>`;
      return;
    }
    this.inspector.classList.remove('is-empty');
    const ground = this.groundSection(t, b !== null);
    if (!b) {
      this.inspector.innerHTML = `
        <div class="panel-head">
          <h2 class="panel-title">Tile</h2>
          <button class="btn icon small" data-action="deselect" aria-label="Clear selection" title="Clear selection (Esc)">×</button>
        </div>
        ${ground}`;
      return;
    }
    const notOperational = b.pendingFunctionPhase ? `Its main function arrives in Phase ${b.pendingFunctionPhase}.` : '';
    const w = b.workers;
    const workersHtml = w
      ? `<div class="workers-row" data-testid="building-workers-row">
          <span class="k">Workers</span>
          ${
            w.editable
              ? `<button class="btn icon stepper" data-action="workers" data-id="${b.id}" data-delta="-1" data-testid="workers-minus" aria-label="One less worker" ${w.current <= 0 ? 'disabled' : ''}>−</button>`
              : ''
          }
          <span class="workers-val" data-testid="building-workers">${w.current} / ${w.slots}</span>
          ${
            w.editable
              ? `<button class="btn icon stepper" data-action="workers" data-id="${b.id}" data-delta="1" data-testid="workers-plus" aria-label="One more worker" ${w.current >= w.slots || !w.canAdd ? 'disabled' : ''}>+</button>`
              : ''
          }
        </div>`
      : '';
    const e = b.economy;
    const econ = [
      e.production ? `<li data-testid="building-production"><span class="k">Produces</span>${escapeHtml(e.production)}</li>` : '',
      e.storage ? `<li data-testid="building-storage"><span class="k">Storage</span>${escapeHtml(e.storage)}</li>` : '',
      e.upkeep ? `<li data-testid="building-upkeep"><span class="k">Upkeep</span>${escapeHtml(e.upkeep)}</li>` : '',
      e.housing ? `<li data-testid="building-housing"><span class="k">Shelter</span>${escapeHtml(e.housing)}</li>` : '',
    ].join('');
    this.inspector.innerHTML = `
      <div class="panel-head">
        <h2 class="panel-title">Building <span class="muted" data-testid="building-id">#${b.id}</span></h2>
        <button class="btn icon small" data-action="deselect" aria-label="Clear selection" title="Clear selection (Esc)">×</button>
      </div>
      <div class="bld-name" data-testid="building-name">${escapeHtml(b.name)}</div>
      <div class="owner-row" data-testid="building-owner"><span class="owner-dot" style="background:${b.owner.css}"></span>${escapeHtml(b.owner.isYou ? 'Your colony' : `${b.owner.name} (${b.owner.id.toUpperCase()})`)}</div>
      <div class="muted bld-meta">${escapeHtml(b.category)} · ${b.footprint.width}×${b.footprint.height} tiles at ${b.origin.x}, ${b.origin.y}</div>
      <div class="bld-state" data-testid="building-state"></div>
      <div class="hp" aria-label="Health"><div class="hp-fill" data-testid="building-hp-fill"></div></div>
      <div class="hp-text muted" data-testid="building-health"></div>
      <p class="desc">${escapeHtml(b.purpose)}</p>
      ${econ ? `<ul class="econ">${econ}</ul>` : ''}
      ${b.defence?.length ? `<ul class="econ" data-testid="building-defence">${b.defence.map((l) => `<li>${escapeHtml(l)}</li>`).join('')}</ul>` : ''}
      ${workersHtml}
      ${b.training ? this.trainingHtml(b.id, b.training) : ''}
      ${b.upgrade ? this.upgradeHtml(b.id, b.upgrade) : ''}
      ${b.repairHint ? `<p class="note" data-testid="repair-hint">${escapeHtml(b.repairHint)}</p>` : ''}
      ${e.production ? `<p class="econ-status" data-testid="building-economy-status">${escapeHtml(b.economyStatus ?? '')}</p>` : ''}
      ${notOperational ? `<p class="note" data-testid="building-operational">${escapeHtml(notOperational)}</p>` : ''}
      <p class="muted small" data-testid="building-cost">Cost: ${escapeHtml(b.cost)}</p>
      ${
        b.owner.isYou && b.demolishable
          ? `<button class="btn danger" data-action="demolish" data-id="${b.id}" data-testid="demolish">Demolish${e.refund ? ` · refund ${escapeHtml(e.refund)}` : ''}</button>`
          : `<p class="start-note" data-testid="start-note">${escapeHtml(
              !b.owner.isYou
                ? `Owned by ${b.owner.name}. You cannot change another colony's buildings.${b.owner.hasController ? '' : ' This seat has no controller yet (no AI until a later phase).'}`
                : `${b.preplaced ? 'Starting structure of your colony. ' : ''}It cannot be demolished.`,
            )}</p>`
      }
      <details class="ground"${this.mode === 'touch' ? '' : ' open'}>
        <summary>Ground at the tile you clicked</summary>
        ${ground}
      </details>`;
    this.updateBuildingLive(b);
  }

  private upgradeHtml(id: number, u: NonNullable<BuildingInspection['upgrade']>): string {
    if (u.done) return `<p class="muted small" data-testid="upgrade-done">Upgraded (level 2).</p>`;
    if (u.progress !== null) {
      return `<div class="upgrade-row" data-testid="upgrade-progress"><span>Upgrading to ${escapeHtml(u.name)}</span><span class="qbar"><span data-testid="upgrade-bar" style="width:${Math.round(100 * u.progress)}%"></span></span></div>`;
    }
    return `<button class="btn upgrade-btn ${u.affordable ? '' : 'is-unaffordable'}" data-action="upgrade" data-id="${id}" data-testid="upgrade" title="Upgrade">⬆ ${escapeHtml(u.name)} · ${escapeHtml(u.cost)} · ${u.seconds} s</button>`;
  }

  /** Live upgrade progress of the shown building (0..1), without re-rendering the card. */
  updateUpgrade(id: number, progress: number): void {
    if (this.shownBuilding !== id) return;
    const bar = this.inspector.querySelector<HTMLElement>('[data-testid="upgrade-bar"]');
    if (bar) bar.style.width = `${Math.round(100 * progress)}%`;
  }

  /** Live production status line of the shown building. */
  updateBuildingEconomy(id: number, status: string): void {
    if (this.shownBuilding !== id) return;
    const el = this.inspector.querySelector<HTMLElement>('[data-testid="building-economy-status"]');
    if (el && el.textContent !== status) {
      el.textContent = status;
      el.classList.toggle('is-stalled', status.startsWith('Storage full'));
    }
  }

  /** Update the changing parts of the building card in place (no re-render, so buttons keep working mid-click). */
  updateBuildingLive(b: Pick<BuildingInspection, 'id' | 'state' | 'progress' | 'health' | 'maxHealth'>): void {
    if (this.shownBuilding !== b.id) return;
    const state = this.inspector.querySelector<HTMLElement>('[data-testid="building-state"]');
    const fill = this.inspector.querySelector<HTMLElement>('[data-testid="building-hp-fill"]');
    const hp = this.inspector.querySelector<HTMLElement>('[data-testid="building-health"]');
    if (!state || !fill || !hp) return;
    const building = b.state === 'under_construction';
    state.textContent = building ? `Under construction · ${Math.floor(b.progress * 100)}%` : 'Complete';
    state.classList.toggle('is-building', building);
    fill.style.width = `${(100 * b.health) / b.maxHealth}%`;
    hp.textContent = `Health ${b.health} / ${b.maxHealth}`;
  }

  destroy(): void {
    this.root.removeEventListener('click', this.onClick);
    this.sizeObserver?.disconnect();
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.root.innerHTML = '';
  }

  private groundSection(t: TileInspection, compact: boolean): string {
    const pc = PASSABILITY_COLORS[t.passability];
    const notes = [
      t.slot !== null ? `<p class="start-note" data-testid="tile-start-note">Start slot ${t.slot + 1} — a colony's Command Center site.</p>` : '',
      t.spawnZone ? '<p class="start-note infected" data-testid="spawn-note">Infected spawn zone — Walkers gather here and hordes come out of it. Nothing can be built here.</p>' : '',
    ].join('');
    return `
      <div class="tile-row">
        <span class="swatch" style="background:${t.swatch}"></span>
        <div>
          <div class="tile-name" data-testid="tile-terrain">${escapeHtml(t.terrain.name)}</div>
          <div class="muted" data-testid="tile-coords">Tile ${t.x}, ${t.y}</div>
        </div>
      </div>
      <ul class="chips">
        <li class="chip ${t.walkable ? 'yes' : 'no'}" data-testid="tile-walkable">${t.walkable ? 'Walkable' : 'Impassable'}</li>
        <li class="chip ${t.buildable ? 'yes' : 'no'}" data-testid="tile-buildable">${t.buildable ? 'Buildable' : 'Not buildable'}</li>
        ${t.edge ? '<li class="chip neutral" data-testid="tile-edge">Map edge</li>' : ''}
        ${t.spawnZone ? '<li class="chip no" data-testid="tile-spawn">Spawn zone</li>' : ''}
      </ul>
      <p class="pass-row" data-testid="tile-passability"><span class="legend-swatch" style="background:${pc.css}"></span>${PASSABILITY_LABEL[t.passability]}</p>
      ${compact ? '' : `<p class="desc">${escapeHtml(t.terrain.description)}</p>`}
      ${notes}`;
  }

  private q<T extends HTMLElement = HTMLElement>(sel: string): T {
    const el = this.root.querySelector<T>(sel);
    if (!el) throw new Error(`HUD element ${sel} missing`);
    return el;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
